import React, { useMemo, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Download,
  HardHat,
  Info,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserRound,
} from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import BadgeBoxCst from "@components/BadgeBoxCst";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import AlertDialogCst from "@components/AlertDialogCst";
import UploadTrainingModalV2Cst from "@components/UploadTrainingModalV2Cst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { useFetchDetail, useFetchDataDetail } from "@hooks/useFetchDetail";
import { ServicesURL, getQueryString, downloadBlobFile } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { CUSTOM_FIELD_KEYS } from "../Wizard/types";
import {
  DetailAccordion,
  DownloadCard,
  downloadGridSx,
  FieldGroup,
  InfoItem,
  SectionHeader,
  searchBoxSx,
  selectBoxSx,
} from "../components/detailLayout";
import { GroupsSection } from "./components/GroupsSection";
import { LearningObjectSection } from "./components/LearningObjectSection";
import { useGetProfileImg } from "@hooks/useGetProfileImg";
import { useDeleteProfileImg } from "@hooks/useDeleteProfileImg";
import {
  MAX_AVATAR_FILE_SIZE,
  useUploadProfileImg,
} from "@hooks/useUploadProfileImg";
import { useScrollToHash } from "@hooks/useScrollToHash";
import { LO_SECTION_ANCHOR, SECTION_ANCHOR_SCROLL_MARGIN } from "../types";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import CardGenericCst from "@components/CardGenericCst";
import {
  SubtitleItem,
  SubtitleRow,
  TitleRow,
} from "@components/ListItemCardCst/style";
import { showAdditionalInfo } from "./utils";
import { ButtonTertiary } from "src/UI/ButtonTertiary";
import { ButtonTertiaryIcon } from "src/UI/ButtonTertiaryIcon";

const PAGE_SIZE = 5;

const formatDate = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";
  const date = new Date(typeof value === "number" ? value : String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("it-IT");
};

const UserDetail: React.FC = () => {
  const { t } = useTranslation("user-detail-v2");
  const { t: tw } = useTranslation("user-wizard-v2");
  const { t: tRoles } = useTranslation("input");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "", userId = "" } = useParams();

  const { imageUrl } = useGetProfileImg(userId);
  const { deleteProfileImg, isDeleting: isDeletingAvatar } =
    useDeleteProfileImg(userId, corporateId);
  const { uploadProfileImg, isUploading: isUploadingAvatar } =
    useUploadProfileImg(userId);

  const avatarFileInputRef = useRef<HTMLInputElement>(null);
  const [avatarValidationError, setAvatarValidationError] = useState("");

  const uploadAvatar = async (file: File) => {
    try {
      await uploadProfileImg(file);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAvatarFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setAvatarValidationError(t("avatarFileTypeError"));
      return;
    }
    if (file.size > MAX_AVATAR_FILE_SIZE) {
      setAvatarValidationError(t("avatarFileSizeError"));
      return;
    }
    void uploadAvatar(file);
  };

  const { dataDetail: user, isLoading } = useFetchDetail(
    ServicesURL.customer,
    `${userId}?corporateId=${corporateId}`,
    PagesID["corporate.customers"],
  );

  const { dataDetail: customFieldsData } = useFetchDataDetail(
    `${ServicesURL.getCorporateCustomFields}?corporate_id=${corporateId}`,
    PagesID["corporate.customFields"],
  );

  const fullName = [user?.name, user?.surname].filter(Boolean).join(" ").trim();
  const isActive = String(user?.status || "").toLowerCase() !== "suspended";

  useScrollToHash(!isLoading);

  const yes = tw("common.yes");

  const roles: string[] = useMemo(() => {
    const raw = user?.roles;
    if (!Array.isArray(raw)) return [];
    const optionsKey = corporateId === "1" ? "roles-digited" : "roles";
    return raw
      .map((r: any) => {
        const value =
          typeof r === "string" ? r : (r?.label ?? r?.value ?? r?.name);
        if (!value) return "";
        return tRoles(
          `options.${optionsKey}.${value.toString().toLowerCase()}`,
          {
            defaultValue: value,
          },
        );
      })
      .filter(Boolean);
  }, [user, corporateId, tRoles]);

  const badges: {
    id: string | number;
    name: string;
    certificateId?: string | number;
    objectId?: string | number;
  }[] = [];
  const initiatives: {
    id: string | number;
    label: string;
    urlDomain?: string;
    urlCorporateDomain?: string;
    status?: string;
  }[] = user?.initiatives ?? [];

  const goToEdit = () =>
    navigate(`/v2/corporate/${corporateId}/detail/users-groups/${userId}/edit`);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [deleteAvatarOpen, setDeleteAvatarOpen] = useState(false);

  const confirmDeleteAvatar = async () => {
    try {
      await deleteProfileImg();
      setDeleteAvatarOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await fetcher(
        getQueryString(`${ServicesURL.deleteCustomer}`, {
          users_id: String(userId),
          corporate_id: corporateId,
        }),
        MethodType.DELETE,
      );
      navigate(`/v2/corporate/${corporateId}/detail/users-groups`);
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const [downloadingCertId, setDownloadingCertId] = useState<
    string | number | null
  >(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const downloadCertificate = async (cert: (typeof badges)[number]) => {
    if (!userId || cert.certificateId == null) return;
    setDownloadingCertId(cert.id);
    try {
      const url = `${ServicesURL.getIssuedCertificate.replace(
        ":certificateId",
        String(cert.certificateId),
      )}?objectId=${cert.objectId ?? ""}&learnerId=${userId}`;
      await downloadBlobFile(
        url,
        null,
        `issued_certificate_lo_${cert.objectId ?? cert.id}_learner_${userId}.pdf`,
        MethodType.GET,
      );
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadingCertId(null);
    }
  };

  const downloadAllCertificates = async () => {
    if (!userId) return;
    const issuedCertificates = badges
      .filter((b) => b.certificateId != null)
      .map((b) => ({
        learnerId: userId,
        objectId: b.objectId ?? b.id,
        issuedCertificateId: b.certificateId,
      }));
    if (issuedCertificates.length === 0) return;
    setDownloadingAll(true);
    try {
      await downloadBlobFile(
        `${ServicesURL.getIssuedCertificatesZip}`,
        { issuedCertificates },
        `issued_certificates_learner_${userId}.zip`,
      );
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadingAll(false);
    }
  };

  const registryItems: InfoItem[] = [
    { label: tw("fields.phone"), value: user?.phone_number },
    { label: tw("fields.address"), value: user?.address },
    { label: tw("fields.gender"), value: user?.gender },
    { label: tw("fields.fiscalCode"), value: user?.fiscal_code },
    { label: tw("fields.birthPlace"), value: user?.birth_country },
    { label: tw("fields.birthProvince"), value: user?.birth_province },
    { label: tw("fields.birthDate"), value: formatDate(user?.birthday) },
  ];

  const companyItems: InfoItem[] = [
    {
      label: tw("questions.newHireLabel"),
      value: user?.new_hire_flag ? yes : "",
    },
    {
      label: tw("questions.smartWorkingLabel"),
      value: user?.smart_working_flag ? yes : "",
    },
    { label: tw("fields.branch"), value: user?.branch },
    { label: tw("fields.language"), value: user?.language },
    { label: tw("fields.atecoCode"), value: user?.ateco_code },
    { label: tw("fields.countryCode"), value: user?.country_code },
    {
      label: tw("fields.countryDescription"),
      value: user?.country_description,
    },
    { label: tw("fields.supervisor"), value: user?.supervisor },
    {
      label: tw("fields.frameworkCode"),
      value: user?.contractual_framework_code,
    },
    {
      label: tw("fields.frameworkType"),
      value: user?.contractual_framework_type,
    },
    { label: tw("fields.contractType"), value: user?.contract_type },
    { label: tw("fields.ccnl"), value: user?.ccnl },
    { label: tw("fields.hireDate"), value: formatDate(user?.hire_date) },
    {
      label: tw("fields.terminationDate"),
      value: formatDate(user?.termination_date),
    },
    { label: tw("fields.scheduleType"), value: user?.schedule_type },
    {
      label: tw("fields.smartLearningHours"),
      value:
        user?.smart_learning_hours != null
          ? String(user.smart_learning_hours)
          : "",
    },
    {
      label: tw("fields.persona"),
      value: Array.isArray(user?.persona) ? user.persona.join(", ") : "",
    },
  ];

  const customCatalog: Record<string, string> = customFieldsData?.catalog || {};
  const customItems: InfoItem[] = CUSTOM_FIELD_KEYS.filter(
    (k) => customCatalog[k],
  ).map((k) => ({ label: customCatalog[k], value: user?.[k] }));

  const uogItems: InfoItem[] = Array.isArray(user?.uog)
    ? user.uog.map((u: any, i: number) => ({
        label: `${t("sections.uog")} ${i + 1}`,
        value: u?.uog_description || u?.uog,
      }))
    : [];

  const [cvSearch, setCvSearch] = useState("");
  const [cvStatus, setCvStatus] = useState("");
  const [cvType, setCvType] = useState("");
  const [cvPage, setCvPage] = useState(1);
  const [cvPageSize, setCvPageSize] = useState(PAGE_SIZE);

  const cvUrl = userId
    ? `${ServicesURL.getSkillBook}/detail?corporateId=${
        corporateId || "1"
      }&learnerId=${userId}${cvStatus ? `&status=${cvStatus}` : ""}`
    : null;
  const {
    items: cvItems,
    totalItems: cvTotal,
    totalPages: cvPages,
    isLoading: loadingCv,
  } = usePaginatedList(
    cvUrl,
    { page: cvPage, pageSize: cvPageSize, search: cvSearch || undefined },
    { responseKey: "content" },
  );

  const { dataDetail: courseTypeData } = useFetchDataDetail(
    ServicesURL.getCourseType,
    PagesID["corporate.customers"],
  );

  const cvTypeOptions = useMemo(() => {
    const raw = Array.isArray(courseTypeData)
      ? courseTypeData
      : courseTypeData?.output || courseTypeData?.content || [];
    return [
      { value: "", label: t("cv.typeAll") },
      ...raw.map((o: any) => ({
        value: String(o.value ?? o.id ?? o.code ?? o.name ?? ""),
        label: String(o.label ?? o.name ?? o.description ?? o.value ?? ""),
      })),
    ];
  }, [courseTypeData, t]);

  const cvStatusOptions = [
    { value: "", label: t("cv.statusAll") },
    { value: "pending", label: t("cv.statusPending") },
    { value: "approved", label: t("cv.statusApproved") },
    { value: "rejected", label: t("cv.statusRejected") },
  ];

  const [uploadOpen, setUploadOpen] = useState(false);

  const filteredCv = useMemo(() => {
    if (!cvType) return cvItems;
    return cvItems.filter(
      (r: any) =>
        String(r.courseType ?? r.trainingType ?? r.type ?? "") === cvType,
    );
  }, [cvItems, cvType]);

  return (
    <AnimationFadeIn>
      <Box
        sx={{
          py: 3,
          pr: 3,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                "& > .MuiBox-root": { position: "static", p: "20px 0" },
              }}
            >
              <Breadcrumb crumbs={[{ to: "", text: t("breadcrumb-detail") }]} />
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              <ButtonTertiaryIcon
                onClick={goToEdit}
                aria-label={t("actions.edit.profile")}
                tooltip={t("actions.edit.profile")}
              >
                <Pencil size={20} />
              </ButtonTertiaryIcon>
              <ButtonTertiaryIcon
                onClick={() => setDeleteOpen(true)}
                aria-label={t("actions.delete.profile")}
                tooltip={t("actions.delete.profile")}
              >
                <Trash2 size={20} />
              </ButtonTertiaryIcon>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              mt: 1,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <BadgeBoxCst variant={isActive ? "success" : "error"}>
                {isActive ? t("status.active") : t("status.suspended")}
              </BadgeBoxCst>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: theme.palette.text.primary,
                  mt: "22px",
                }}
              >
                ID: {userId}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  mt: 2,
                }}
              >
                <Typography sx={{ fontSize: "28px", fontWeight: 700 }}>
                  {isLoading ? "…" : fullName || "-"}
                </Typography>
                <Typography
                  component={user?.email ? "a" : "span"}
                  href={user?.email ? `mailto:${user.email}` : undefined}
                  sx={{
                    fontSize: "14px",
                    color: theme.palette.text.primary,
                    textDecoration: user?.email ? "underline" : "none",
                    cursor: user?.email ? "pointer" : "default",
                    opacity: 0.8,
                    width: "fit-content",
                    "&:hover": user?.email
                      ? {
                          color: theme.palette.text.primary,
                          opacity: 1,
                        }
                      : undefined,
                  }}
                >
                  {user?.email || ""}
                </Typography>
                {roles.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <HardHat size={18} color={theme.palette.text.primary} />
                    <Typography sx={{ fontSize: "15px" }}>
                      {roles.join(", ")}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                position: "relative",
                width: "14rem",
                height: "14rem",
                borderRadius: "12px",
                border: `1px solid ${theme.palette.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.action.hover,
                overflow: "hidden",
                "&:hover .avatar-overlay": {
                  opacity: 1,
                },
                "&:hover .avatar-actions": {
                  opacity: 1,
                },
              }}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={fullName}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <UserRound size={48} />
              )}
              <input
                ref={avatarFileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleAvatarFileChange}
              />
              {imageUrl && (
                <Box
                  className="avatar-overlay"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    opacity: 0,
                    transition: "opacity 0.2s ease-in-out",
                    pointerEvents: "none",
                  }}
                />
              )}
              <Box
                className="avatar-actions"
                sx={{
                  position: "absolute",
                  top: "0.5rem",
                  right: "0.5rem",
                  display: "flex",
                  gap: 1,
                  opacity: 0,
                  transition: "opacity 0.2s ease-in-out",
                }}
              >
                <ButtonTertiaryIcon
                  onClick={() => avatarFileInputRef.current?.click()}
                  disabled={isUploadingAvatar || isDeletingAvatar}
                  aria-label={t("actions.edit.avatar")}
                  tooltip={t("actions.edit.avatar")}
                >
                  <Pencil size={20} />
                </ButtonTertiaryIcon>
                {imageUrl && (
                  <ButtonTertiaryIcon
                    onClick={() => setDeleteAvatarOpen(true)}
                    disabled={isUploadingAvatar || isDeletingAvatar}
                    aria-label={t("actions.delete.avatar")}
                    tooltip={t("actions.delete.avatar")}
                  >
                    <Trash2 size={20} />
                  </ButtonTertiaryIcon>
                )}
              </Box>
              {(isUploadingAvatar || isDeletingAvatar) && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                  }}
                >
                  <CircularProgress
                    size={32}
                    sx={{ color: theme.palette.systemSecondary01 }}
                  />
                </Box>
              )}
              {imageUrl && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    height: "2rem",
                    padding: "0.25rem 0.5rem",
                    backgroundColor:
                      theme.palette.backgroundDisabledAlternative,
                    color: theme.palette.exploreCardText,
                  }}
                >
                  <Info
                    size={"1.05rem"}
                    color={theme.palette.exploreCardText}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      lineHeight: "0.75rem",
                      fontWeight: 600,
                      color: theme.palette.exploreCardText,
                      width: "10rem",
                    }}
                  >
                    {t("avatarUploadInfo")}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        {showAdditionalInfo(
          registryItems,
          companyItems,
          customItems,
          uogItems,
        ) && (
          <DetailAccordion
            title={t("additionalInfo")}
            sxAccordion={{
              "& .MuiAccordionSummary-root": {
                minHeight: "2.2rem",
                position: "relative",
                "&.Mui-expanded::after": {
                  content: '""',
                  position: "absolute",
                  left: "0.5rem",
                  right: "0.5rem",
                  bottom: 0,
                  height: "1px",
                  backgroundColor: theme.palette.divider,
                },
              },
              "& .MuiAccordionSummary-content": {
                mt: "0.25rem !important",
                mb: "0.25rem !important",
              },
            }}
          >
            {isLoading ? (
              <CircularProgress />
            ) : (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
              >
                <FieldGroup
                  title={t("sections.registry")}
                  items={registryItems}
                />
                <FieldGroup
                  title={tw("sections.companyInfo")}
                  items={companyItems}
                />
                <FieldGroup
                  title={tw("sections.otherInfo")}
                  items={customItems}
                />
                <FieldGroup title={t("sections.uogTitle")} items={uogItems} />
              </Box>
            )}
          </DetailAccordion>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader
            sx={{ mb: 1 }}
            title={t("badges.title")}
            action={
              <>
                {badges.length > 0 && (
                  <Button
                    variant="outlined"
                    startIcon={
                      downloadingAll ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Download size={16} />
                      )
                    }
                    onClick={downloadAllCertificates}
                    disabled={badges.length === 0 || downloadingAll}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      fontWeight: 600,
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    {t("badges.downloadAll")}
                  </Button>
                )}
              </>
            }
          />
          {badges.length > 0 ? (
            <Box sx={downloadGridSx}>
              {badges.map((b) => (
                <DownloadCard
                  key={b.id}
                  title={b.name}
                  id={b.id}
                  loading={downloadingCertId === b.id}
                  onDownload={() => downloadCertificate(b)}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: theme.palette.text.primary }}>
              {t("badges.empty")}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader sx={{ mb: 1 }} title={t("initiatives.title")} />
          {/* TODO andrebbe fatto un componente apposito per la sezione e liberare spazio e logica dal padre, come groupSection e LearningObjectSection sottostanti */}
          {initiatives.length > 0 ? (
            initiatives.map((row, index) => (
              <CardGenericCst
                key={row.id ?? index}
                showAccent={true}
                left={
                  <>
                    <TitleRow sx={{}}>
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          lineHeight: 1.3,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {row.label}
                      </Typography>
                    </TitleRow>
                    <SubtitleRow>
                      <SubtitleItem>
                        <span>ID: {row.id}</span>
                      </SubtitleItem>
                    </SubtitleRow>
                  </>
                }
                right={<>{row.status}</>} //mettere badge in base allo status
              />
            ))
          ) : (
            <Typography sx={{ color: theme.palette.text.primary }}>
              {t("initiatives.empty")}
            </Typography>
          )}
        </Box>

        <GroupsSection user={user} />

        <Box
          id={LO_SECTION_ANCHOR}
          sx={{ scrollMarginTop: SECTION_ANCHOR_SCROLL_MARGIN }}
        >
          <LearningObjectSection />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader
            sx={{ mb: 1 }}
            title={t("cv.title")}
            subtitle={t("cv.subtitle")}
            action={
              <ButtonPrimary
                endIcon={<Plus size={16} />}
                onClick={() => setUploadOpen(true)}
              >
                {t("cv.uploadExternal")}
              </ButtonPrimary>
            }
          />
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={searchBoxSx}>
              <InputCst
                id="user-detail-cv-search"
                label={t("search")}
                value={cvSearch}
                onChange={(e: any) => {
                  setCvSearch(e.target.value);
                  setCvPage(1);
                }}
                endAdornment={
                  <Search size={16} color={theme.palette.text.tertiary} />
                }
              />
            </Box>
            <Box sx={selectBoxSx}>
              <SelectCst
                id="user-detail-cv-status"
                label={t("cv.status")}
                value={cvStatus}
                options={cvStatusOptions}
                onChange={(value: any) => {
                  setCvStatus(value);
                  setCvPage(1);
                }}
              />
            </Box>
            <Box sx={selectBoxSx}>
              <SelectCst
                id="user-detail-cv-type"
                label={t("cv.type")}
                value={cvType}
                options={cvTypeOptions}
                onChange={(value: any) => setCvType(value)}
              />
            </Box>
          </Box>

          {loadingCv ? (
            <CircularProgress />
          ) : filteredCv.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.primary }}>
              {t("cv.empty")}
            </Typography>
          ) : (
            filteredCv.map((raw: any, index: number) => {
              const cvId = raw.id ?? raw.requestId ?? index;
              const subParts = [
                raw.courseType || raw.trainingType || raw.type,
                raw.status,
                raw.year,
              ]
                .filter(Boolean)
                .join(" | ");
              return (
                <ListItemCardCst
                  key={cvId}
                  title={
                    raw.courseName ||
                    raw.name ||
                    raw.title ||
                    raw.trainingName ||
                    "-"
                  }
                  subtitle={{ id: cvId, activityType: subParts }}
                  badges={[]}
                  actions={[]}
                />
              );
            })
          )}

          {cvPages > 1 && !cvType && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PaginatorCst
                page={cvPage}
                pageSize={cvPageSize}
                totalItems={cvTotal}
                onPageChange={setCvPage}
                onPageSizeChange={(next) => {
                  setCvPageSize(next);
                  setCvPage(1);
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <ConfirmDialogCst
        open={deleteOpen}
        title={String(t("delete-item", { ns: "modal" }))}
        description={t("confirm-delete", { ns: "modal" })}
        confirmLabel={String(
          t("confirm-dialog.confirm", { ns: "users-groups-v2" }),
        )}
        cancelLabel={String(
          t("confirm-dialog.cancel", { ns: "users-groups-v2" }),
        )}
        confirmVariant="danger"
        isSubmitting={isDeleting}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
      />

      <ConfirmDialogCst
        open={deleteAvatarOpen}
        title={String(t("actions.delete.avatar"))}
        description={t("confirm-delete", { ns: "modal" })}
        confirmLabel={String(
          t("confirm-dialog.confirm", { ns: "users-groups-v2" }),
        )}
        cancelLabel={String(
          t("confirm-dialog.cancel", { ns: "users-groups-v2" }),
        )}
        confirmVariant="danger"
        isSubmitting={isDeletingAvatar}
        onClose={() => setDeleteAvatarOpen(false)}
        onConfirm={confirmDeleteAvatar}
      />

      <AlertDialogCst
        open={Boolean(avatarValidationError)}
        title={String(t("warning-title", { ns: "modal" }))}
        description={avatarValidationError}
        closeLabel={t("avatarValidationClose")}
        onClose={() => setAvatarValidationError("")}
      />

      <UploadTrainingModalV2Cst
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
      />
    </AnimationFadeIn>
  );
};

export default UserDetail;
